/**
 * Canale: entità di prima classe (specifica tecnica §4.2).
 * La centralina dichiara i propri canali all'avvio e l'interfaccia si costruisce
 * da quel descrittore: il numero di zone o di sonde non è mai una costante.
 */

export type DirezioneCanale = 'INPUT' | 'OUTPUT';

export type Grandezza = 'TEMPERATURA' | 'UMIDITA' | 'LUMINOSITA' | 'POTENZA' | 'COMANDO' | 'MODULAZIONE';

export type TipoModulo =
  | 'UNITA_CENTRALE'
  | 'RELE_230V_16A'
  | 'PWM_0_10V'
  | 'NTC_10K'
  | 'PT1000'
  | 'MULTIPLEX'
  | 'LAN'
  | 'WIFI'
  | 'CONNETTORE';

export type TipoSonda = 'NTC_10K' | 'PT1000' | 'ANALOGICO' | 'DIGITALE';

/** Livello di confidenza dell'assegnazione (docs/reference/io-map.json). */
export type Confidenza = 'alta' | 'media' | 'bassa';

/** Funzioni d'impianto a cui un canale può essere assegnato (specifica §5). */
export type FunzioneImpianto =
  | 'MANDATA_COLLETTORE'
  | 'RITORNO_COLLETTORE'
  | 'ACCUMULO_SANITARIO'
  | 'MANDATA_GENERATORE'
  | 'RITORNO_GENERATORE'
  | 'PUFFER_ALTO'
  | 'PUFFER_BASSO'
  | 'TEMPERATURA_ESTERNA'
  | 'UMIDITA_INTERNA'
  | 'LUMINOSITA'
  | 'VMC'
  | 'LOGICA_GENERATORE'
  | 'CIRCOLATORE'
  | 'VALVOLA_DEVIATRICE'
  | 'COMANDO_GENERATORE';

export const FUNZIONI_IMPIANTO: readonly FunzioneImpianto[] = [
  'MANDATA_COLLETTORE',
  'RITORNO_COLLETTORE',
  'ACCUMULO_SANITARIO',
  'MANDATA_GENERATORE',
  'RITORNO_GENERATORE',
  'PUFFER_ALTO',
  'PUFFER_BASSO',
  'TEMPERATURA_ESTERNA',
  'UMIDITA_INTERNA',
  'LUMINOSITA',
  'VMC',
  'LOGICA_GENERATORE',
  'CIRCOLATORE',
  'VALVOLA_DEVIATRICE',
  'COMANDO_GENERATORE',
];

export type Assegnazione =
  | { tipo: 'zona'; zonaId: string }
  | { tipo: 'funzione'; funzione: FunzioneImpianto; generatoreId?: string }
  | { tipo: 'nessuna' };

export interface Taratura {
  /** Sommato al valore convertito. */
  offset: number;
  /** Moltiplica il valore convertito prima dell'offset. */
  fattore: number;
}

export interface Modulo {
  id: string;
  tipo: TipoModulo;
  nome: string;
  stato: 'ok' | 'assente' | 'guasto';
  /** Per i connettori di terze parti: la sorgente da cui il modulo legge. */
  sorgenteId?: string;
}

export interface Canale {
  /** Sigla del pin fisico (`A5`, `22`) o, per un canale multiplexato, `A11/2`. */
  id: string;
  moduloId: string;
  direzione: DirezioneCanale;
  grandezza: Grandezza;
  unita: string;
  tipoSonda?: TipoSonda;
  taratura: Taratura;
  assegnazione: Assegnazione;
  confidenza?: Confidenza;
  /** Nota del materiale di riferimento (es. "2 locali su un'unica sonda"). */
  nota?: string;
}

export const TARATURA_NEUTRA: Taratura = { offset: 0, fattore: 1 };

export function applicaTaratura(grezzo: number, taratura: Taratura): number {
  return grezzo * taratura.fattore + taratura.offset;
}
